import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as R from 'remeda';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, mapTo, tap } from 'rxjs/operators';
import { environment } from '~environments/environment';
import { FracClusterTruckingVendor, FracClusterTruckingVendorStatus } from '~lmo/models/frac.model';
import { AllocationPercent, TruckingVendorRequest, TruckingVendorUpdate } from '~lmo/models/trucking-vendor.model';
import { ErrorHandlingService } from '~services/error-handling.service';
import { RouterStateService } from '~services/router-state.service';
import { idArrayToRecord } from '~utilities/idArrayToRecord';
import * as fromRouterConstants from '../lmo-routing.constants';
import { LmoFracsService } from './lmo-fracs.service';

export interface OverflowVendorContract {
  contractId: number;
  contractName: string;
  lastUsedTimestamp: string;
  currentOverflowFCTVId: number;
  isCurrentClusterOverflowVendor: boolean;
}

export interface OverflowVendor {
  vendorId: number;
  vendorName: string;
  contracts: OverflowVendorContract[];
}

export interface OverflowUpdate {
  fctvId?: number;
  vendorContractId: number;
}

interface FctvResponse {
  allocationPercents: AllocationPercent[];
  fracClusterTruckingVendor: FracClusterTruckingVendor;
}

export const fctvSorter = R.sort((a: FracClusterTruckingVendor, b: FracClusterTruckingVendor) => {
  if (a.allocationPercent < b.allocationPercent) {
    return 1;
  }
  if (a.allocationPercent > b.allocationPercent) {
    return -1;
  }
  return a.truckingVendorName < b.truckingVendorName ? -1 : 1;
});

@Injectable({
  providedIn: 'root',
})
export class LmoFracFctvsService {
  private currentClusterId: number;
  private currentFracFctvs$$: BehaviorSubject<Record<number, FracClusterTruckingVendor>> = new BehaviorSubject<
    Record<number, FracClusterTruckingVendor>
  >({});
  private currentFctv$$: BehaviorSubject<FracClusterTruckingVendor> = new BehaviorSubject<FracClusterTruckingVendor>(
    null,
  );
  private currentFracOverflowVendors$$: BehaviorSubject<OverflowVendor[]> = new BehaviorSubject<OverflowVendor[]>([]);

  public get currentFracFctvs$(): Observable<FracClusterTruckingVendor[]> {
    return this.currentFracFctvs$$.asObservable().pipe(
      map((fctvsRecord) => Object.keys(fctvsRecord).map((key) => fctvsRecord[key])),
      map(fctvSorter),
    );
  }

  public get currentFctv$(): Observable<FracClusterTruckingVendor> {
    return this.currentFctv$$.asObservable();
  }

  public get currentFracOverflowVendors$(): Observable<OverflowVendor[]> {
    return combineLatest([this.currentFracOverflowVendors$$.asObservable(), this.currentFracFctvs$]).pipe(
      map(([possibleOverflowVendors, currentFCTVs]) => {
        return possibleOverflowVendors.filter((possibleVendor) => {
          const fctvVendor: FracClusterTruckingVendor = currentFCTVs.find(
            (fctv) =>
              fctv.truckingVendorId === possibleVendor.vendorId &&
              (fctv.status === 'awarded' || fctv.status === 'pending'),
          );
          // This is not an existing FCTV
          if (!fctvVendor) {
            return true;
          }

          return fctvVendor.allocationPercent === 0;
        });
      }),
    );
  }

  constructor(
    private http: HttpClient,
    private fracService: LmoFracsService,
    private errorService: ErrorHandlingService,
    private routerState: RouterStateService,
    private snackBar: MatSnackBar,
  ) {
    this.fracService.currentFrac$
      .pipe(
        distinctUntilChanged((a, b) => {
          if (!a && !b) {
            return true;
          }
          if (a && !b) {
            return false;
          }
          if (b && !a) {
            return false;
          }
          return (
            a.id === b.id && (a.fracClusterTruckingVendors || []).length === (b.fracClusterTruckingVendors || []).length
          );
        }),
      )
      .subscribe((currentFrac) => {
        if (!currentFrac) {
          this.currentClusterId = null;
          this.currentFracFctvs$$.next({});
          this.currentFracOverflowVendors$$.next([]);
          return;
        }
        this.currentClusterId = currentFrac.cluster.id;
        this.loadFctvOverflows(currentFrac.cluster.id);
        if (currentFrac.fracClusterTruckingVendors) {
          this.currentFracFctvs$$.next(idArrayToRecord(currentFrac.fracClusterTruckingVendors));
        }
      });
    this.subscribeToRouterFctvIdChanges();
  }

  public createFctv$(request: TruckingVendorRequest): Observable<boolean> {
    return this.http.post(`${environment.api}/v2/frac_cluster_trucking_vendor`, request).pipe(
      tap((fctvResponse: FctvResponse) => {
        this.updateCurrentFracFctvs(fctvResponse);
        this.snackBar.open(`${fctvResponse.fracClusterTruckingVendor.truckingVendorName} has been created`, null, {
          duration: 5000,
        });
      }),
      map(() => true),
      catchError((error) => {
        this.errorService.showError(error);
        console.log(error);
        return of(false);
      }),
    );
  }

  public updateFctv$(fctvId: number, request: TruckingVendorUpdate): Observable<boolean> {
    return this.http.patch(`${environment.api}/v2/frac_cluster_trucking_vendor/${fctvId}`, request).pipe(
      tap((fctvResponse: FctvResponse) => {
        this.updateCurrentFracFctvs(fctvResponse);
        this.snackBar.open(`${fctvResponse.fracClusterTruckingVendor.truckingVendorName} has been updated`, null, {
          duration: 5000,
        });
      }),
      map(() => true),
      catchError((error) => {
        this.errorService.showError(error);
        console.log(error);
        return of(false);
      }),
    );
  }

  public rejectFctv$(fctvId: number, status: FracClusterTruckingVendorStatus): Observable<boolean> {
    return this.http.patch(`${environment.api}/frac_cluster_trucking_vendor/${fctvId}/reject`, null).pipe(
      tap((fracClusterTruckingVendor: FracClusterTruckingVendor) => {
        if (this.currentClusterId === fracClusterTruckingVendor.fracClusterId) {
          this.currentFracFctvs$$.next({
            ...this.currentFracFctvs$$.value,
            [fracClusterTruckingVendor.id]: fracClusterTruckingVendor,
          });
        }
        const actionVerb = status === 'awarded' ? 'removed' : 'cancelled';
        this.snackBar.open(`${fracClusterTruckingVendor.truckingVendorName} has been ${actionVerb}`, null, {
          duration: 5000,
        });
      }),
      map(() => true),
      catchError((error) => {
        this.errorService.showError(error);
        console.log(error);
        return of(false);
      }),
    );
  }

  private loadFctvOverflows(fracClusterId: number): void {
    this.http
      .get<OverflowVendor[]>(
        `${environment.api}/frac_cluster/${fracClusterId}/frac_cluster_trucking_vendor/overflow_vendors`,
      )
      .subscribe((data) => {
        data.sort((a, b) => a.vendorName.toLocaleLowerCase().localeCompare(b.vendorName.toLocaleLowerCase()));
        this.currentFracOverflowVendors$$.next(data);
      });
  }

  public updateFctvOverflows$(updates: OverflowUpdate[], fctvIdsToRemove: number[]): Observable<boolean> {
    return this.http
      .put<OverflowVendor[]>(
        `${environment.api}/frac_cluster/${this.currentClusterId}/frac_cluster_trucking_vendor/overflow_vendors`,
        { updates, fctvIdsToRemove },
      )
      .pipe(
        tap((data) => {
          data.sort((a, b) => a.vendorName.toLocaleLowerCase().localeCompare(b.vendorName.toLocaleLowerCase()));
          this.currentFracOverflowVendors$$.next(data);
        }),
        tap(() => {
          this.snackBar.open(`Overflow Vendors Updated`, null, {
            duration: 2000,
          });
        }),
        mapTo(true),
        catchError((error) => {
          this.errorService.showError(error);
          console.log(error);
          return of(false);
        }),
      );
  }

  private updateCurrentFracFctvs({ allocationPercents, fracClusterTruckingVendor }: FctvResponse): void {
    if (this.currentClusterId === fracClusterTruckingVendor.fracClusterId) {
      const updatedFctvs = { ...this.currentFracFctvs$$.value };
      updatedFctvs[fracClusterTruckingVendor.id] = fracClusterTruckingVendor;
      allocationPercents.forEach((percent) => {
        const fctv = updatedFctvs[percent.fracClusterTruckingVendorId];
        if (fctv) {
          updatedFctvs[percent.fracClusterTruckingVendorId] = {
            ...fctv,
            allocationPercent: percent.allocationPercent,
          };
        }
      });
      this.currentFracFctvs$$.next(updatedFctvs);
    }
  }

  private subscribeToRouterFctvIdChanges() {
    combineLatest([
      this.routerState.listenForParamChange$(fromRouterConstants.FCTV_ID),
      this.currentFracFctvs$$,
    ]).subscribe(([fcpoId, fctvsRecord]) => {
      if (!fcpoId) {
        this.currentFctv$$.next(null);
        return;
      }
      const localCopyOfFcpo: FracClusterTruckingVendor = fctvsRecord[fcpoId];
      if (localCopyOfFcpo) {
        this.currentFctv$$.next(localCopyOfFcpo);
      } else {
        this.currentFctv$$.next(null);
      }
    });
  }
}
